import { useMemo, useState } from 'react'
import { useFormContext, type UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import AddIcon from '@/assets/icons/add.svg?react'
import Button from '@/components/Button/Button'
import FormField from '@/components/FormField'
import Input from '@/components/Input/Input'
import InputNumber from '@/components/InputNumber'
import Textarea from '@/components/Textarea/Textarea'
import {
  FormButtons,
  FormLayout,
  FormSection,
  FormSectionFields,
  FormSectionHeader
} from '@/components/common/FormGrid/FormGrid'
import type { AvailabilityForm } from '@/constants/availability-form'
import { daysOfWeek } from '@/constants/days-of-week'
import type { TimetableSettings } from '@/modules/timetable'
import {
  AvailabilityDataBlock,
  type AvailabilityDataBlockProps
} from '@/modules/users'

import styles from './RoomForm.module.scss'
import PreferredClassesSelect from '../components/PreferredClassesSelect'
import PreferredCoursesSelect from '../components/PreferredCoursesSelect'
import PreferredUsersSelect from '../components/PreferredUsersSelect'
import RoomAvailabilityModal from '../components/RoomAvailabilityModal'
import RoomTypeSelect from '../components/RoomTypeSelect'
import type { RoomFormPayload } from '../constants/room-payload'

type RoomFormProps = {
  form: UseFormReturn<RoomFormPayload>
  onSubmit: (data: RoomFormPayload) => void
  workingHours?: TimetableSettings
  withoutAvailability?: boolean
}

const parseAvailabilityFormValue = (
  availabilityFormValue: RoomFormPayload['availability']
): AvailabilityDataBlockProps['availability'] => {
  if (!availabilityFormValue) return

  return daysOfWeek.reduce(
    (acc, day) => ({
      ...acc,
      [day]: availabilityFormValue[day].map(({ time, isOptional }) => ({
        id: `${day}-${time.from}-${time.to}`,
        startTime: time.from,
        endTime: time.to,
        isOptional
      }))
    }),
    {} as Exclude<AvailabilityDataBlockProps['availability'], undefined>
  )
}

const RoomForm = (props: RoomFormProps) => {
  const { t } = useTranslation('rooms')

  return (
    <>
      <FormLayout form={props.form} onSubmit={props.onSubmit}>
        <FormSection header={t('header.basic-information')}>
          <FormField
            id="name"
            name="name"
            label={t('label.name')}
            control={props.form.control}
            required
            render={({ inputProps }) => (
              <Input
                {...inputProps}
                placeholder={t('placeholder.type-name')}
                maxLength={80}
              />
            )}
          />
          <FormField
            id="capacity"
            name="capacity"
            label={t('label.capacity')}
            control={props.form.control}
            render={({ inputProps }) => (
              <InputNumber
                {...inputProps}
                placeholder={t('placeholder.select-capacity-number')}
              />
            )}
          />
          <FormField
            id="type"
            name="type"
            label={t('label.type')}
            control={props.form.control}
            required
            render={({ inputProps }) => <RoomTypeSelect {...inputProps} />}
          />
        </FormSection>

        <FormSection header={t('header.special-equipment')}>
          <FormField
            className={styles.allCols}
            id="specialEquipment"
            name="specialEquipment"
            label={t('label.equipment')}
            control={props.form.control}
            render={({ inputProps }) => (
              <Textarea
                {...inputProps}
                className={styles.textarea}
                placeholder={t('placeholder.type-special-equipment')}
              />
            )}
          />
        </FormSection>

        <FormSection header={t('header.room-preferences')}>
          <FormField
            id="preferredCourses"
            name="preferredCourses"
            label={t('label.preferred-course')}
            control={props.form.control}
            render={({ inputProps }) => (
              <PreferredCoursesSelect {...inputProps} />
            )}
          />
          <FormField
            id="preferredUsers"
            name="preferredUsers"
            label={t('label.preferred-user')}
            control={props.form.control}
            render={({ inputProps }) => (
              <PreferredUsersSelect {...inputProps} />
            )}
          />
          <FormField
            id="preferredClasses"
            name="preferredClasses"
            label={t('label.preferred-class')}
            control={props.form.control}
            render={({ inputProps }) => (
              <PreferredClassesSelect {...inputProps} />
            )}
          />
        </FormSection>

        {!props.withoutAvailability ? (
          <AvailabilitySection workingHours={props.workingHours} />
        ) : null}

        <FormButtons />
      </FormLayout>
    </>
  )
}

const AvailabilitySection = (props: { workingHours?: TimetableSettings }) => {
  const { t } = useTranslation('rooms')

  const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] = useState(false)
  const form = useFormContext<RoomFormPayload>()

  const openModal = () => setIsAvailabilityModalOpen(true)

  const handleAvailabilityModalSubmit = (data: AvailabilityForm) => {
    form.setValue('availability', data)
    setIsAvailabilityModalOpen(false)
  }

  const availabilityFormValue = form.watch('availability')

  const availability = useMemo(
    () => parseAvailabilityFormValue(availabilityFormValue),
    [availabilityFormValue]
  )

  return (
    <>
      <FormSectionFields>
        <div className={styles.header}>
          <FormSectionHeader>{t('header.availability')}</FormSectionHeader>
          <Button variant="secondary" onClick={openModal} icon={<AddIcon />}>
            {t('button.set-availability')}
          </Button>
        </div>

        <AvailabilityDataBlock availability={availability} />
      </FormSectionFields>

      {props.workingHours && availabilityFormValue ? (
        <RoomAvailabilityModal
          onSubmit={handleAvailabilityModalSubmit}
          open={isAvailabilityModalOpen}
          onOpenChange={setIsAvailabilityModalOpen}
          workingHours={props.workingHours}
          defaultValues={availabilityFormValue}
        />
      ) : null}
    </>
  )
}

export default RoomForm
